import { DateTime } from 'luxon';
import { MeetingPriority, MeetingStatus } from '../../../store/schedule/types';
import type { Meeting, MeetingCreate, MeetingUpdate, MeetingSlot } from '../../../store/schedule/types';
import type { TimeZone } from '../../../utils/scheduleUtils';
import { PROVIDER } from '../../../constants';
import { transformMarkupToSimpleTemplate, transformMarkupToText } from '@CabComponents/CabTextTokenInput';
import type { MeetingSettingFormInput, LocationsFormInput } from './types';

interface MeetingUpdateContext {
  meeting: Pick<Meeting, 'id'> & Partial<Pick<Meeting, 'duration_minutes' | 'start_delay_minutes'>>;
  showTokenInputs: boolean;
}

interface MeetingCreateContext {
  meeting: Partial<Meeting> & Pick<Meeting, 'id'>;
  meetingSlots: MeetingSlot[];
  calendarTimezone?: TimeZone;
  secondaryTimezones?: (TimeZone | undefined)[];
  showTokenInputs: boolean;
  isPoll?: boolean;
  isReusable?: boolean;
  isRecurring?: boolean;
  isDirectEvent?: boolean;
  participantCount: number;
  allowRescheduleCancel?: boolean;
  locations?: LocationsFormInput['locations'];
}

export const transformLocations = (locations: LocationsFormInput['locations']) => {
  const [conferenceLocation] = locations?.filter(loc => loc?.conference) || [];
  const conferenceProvider: number | null | undefined = conferenceLocation?.conference?.provider || null;
  const conferenceLeader: number | null | undefined = conferenceLocation?.conference?.leader || null;

  const submitLocations = locations?.filter(loc => loc?.locationText != null)
    .map(loc => loc?.locationText ? transformMarkupToSimpleTemplate(loc.locationText) : '') as string[];
  const rooms = locations?.filter(loc => loc?.room != null && loc.room !== -1)
    .map(loc => loc?.room) as number[];
  const submitPresetLocations = locations?.filter(loc => loc?.presetLocation != null)
    .map(loc => loc?.presetLocation) as number[];

  const conferenceSettings = conferenceLocation?.conference?.settings;
  const zoomConferenceSettings = conferenceProvider === PROVIDER.ZOOM.id
    && conferenceSettings?.provider === 3
    ? {
      alternate_hosts: conferenceSettings.alternativeHosts ?? [],
      auto_recording: conferenceSettings.autoRecording ?? "none",
      join_before_host: conferenceSettings.joinBeforeHost ?? false,
    }
    : null;

  return {
    conference_provider: conferenceProvider,
    conference_leader: conferenceLeader,
    locations: submitLocations,
    rooms,
    location_presets: submitPresetLocations,
    zoom_conference_settings: zoomConferenceSettings,
  };
};

export const buildMeetingCreatePayload = (
  data: MeetingSettingFormInput,
  { meeting, meetingSlots, calendarTimezone, secondaryTimezones, showTokenInputs, isPoll, isReusable, isRecurring,
    isDirectEvent, participantCount, allowRescheduleCancel, locations }: MeetingCreateContext,
): MeetingCreate => {
  const locs = isDirectEvent ? transformLocations(locations) : undefined;
  let duration = data.duration;
  if (isDirectEvent && meetingSlots[0]) {
    duration = DateTime.fromISO(meetingSlots[0].end_date)
      .diff(DateTime.fromISO(meetingSlots[0].start_date)).as('minutes');
  }

  return {
    title: showTokenInputs ? transformMarkupToSimpleTemplate(data.meetingName) : data.meetingName,
    title_booked: null,
    internal_label: isReusable
      ? (data.internalMeetingLabel || transformMarkupToText(data.meetingName))
      : undefined,
    description: showTokenInputs ? transformMarkupToSimpleTemplate(data.description) : data.description,
    description_booked: null,
    notes: data.notes || null,
    duration_minutes: duration,
    num_expected_participants: Math.max(data.attendees, participantCount),
    executive_hold: data.executiveHoldCalendars.length > 0,
    hold_calendars: data.executiveHoldCalendars,
    leaders: meeting.leader_info?.map(leader => leader.id) || [],
    create_date: '',
    date_scheduled: null,
    event_start_time: null,
    selected_scheduled_time: null,
    event_id: null,
    calendar_tz: calendarTimezone?.name || null,
    secondary_tz: secondaryTimezones?.map(tz => tz?.name || null) || [],
    copy_tz: null,
    secondary_copy_tz: null,
    status: MeetingStatus.PENDING,
    prevent_conflict: data.preventDoubleBookings,
    is_poll: isPoll,
    errors: [],
    error_count: 0,
    show_voter_names: true,
    allow_add_participants: data.allowAddParticipants,
    rooms: locs?.rooms || meeting.rooms || [],
    location_presets: locs?.location_presets || meeting.location_presets || [],
    questions: meeting.questions || {},
    auto_merge_slots: !data.selectExactTimes,
    is_reusable: isReusable || false,
    is_recurring: isRecurring || false,
    enable_public_attendee_list: meeting.enable_public_attendee_list || false,
    allow_reschedule_cancel: allowRescheduleCancel || false,
    recurring_times: data.recurringTimes || null,
    priority: MeetingPriority.MEDIUM,
    start_delay_minutes: data.startDelay,
    files: [],
    buffer_start_minutes: data.bufferStartMinutes,
    buffer_end_minutes: data.bufferEndMinutes,
    all_day_slots: data.allDaySlots,
    book_on_create: isDirectEvent ? true : undefined,
    booking_calendar: isDirectEvent ? data.selectedCalendar : undefined,
    private_visibility: data.privateVisibility,
    ...locs,
  };
};

const buildCommonUpdateFields = (
  data: Partial<MeetingSettingFormInput>, { meeting, showTokenInputs }: MeetingUpdateContext,
): MeetingUpdate => ({
  id: meeting.id,
  title: showTokenInputs && data.meetingName != null
    ? transformMarkupToSimpleTemplate(data.meetingName)
    : data.meetingName,
  description: showTokenInputs && data.description != null
    ? transformMarkupToSimpleTemplate(data.description)
    : data.description,
  notes: data.notes || null,
  duration_minutes: data.duration || meeting.duration_minutes,
  prevent_conflict: data.preventDoubleBookings,
  hold_calendars: data.executiveHoldCalendars,
  auto_merge_slots: data.allDaySlots ? true : (data.selectExactTimes == null ? undefined : !data.selectExactTimes),
  start_delay_minutes: data.startDelay || meeting.start_delay_minutes,
  all_day_slots: data.allDaySlots,
});

export const buildMeetingUpdatePayload = (
  data: MeetingSettingFormInput, context: MeetingUpdateContext & { isReusable?: boolean; isRecurring?: boolean },
): MeetingUpdate => ({
  ...buildCommonUpdateFields(data, context),
  internal_label: context.isReusable
    ? (data.internalMeetingLabel || transformMarkupToText(data.meetingName))
    : undefined,
  num_expected_participants: data.attendees,
  executive_hold: data.executiveHoldCalendars.length > 0,
  recurring_times: data.recurringTimes || null,
  is_recurring: context.isRecurring || false,
});

// Autosave receives sparse form data; preserve its existing null/undefined and participant-count behavior.
export const buildMeetingAutoSavePayload = (
  data: Partial<MeetingSettingFormInput>, context: MeetingUpdateContext & { participantCount: number },
): MeetingUpdate => ({
  ...buildCommonUpdateFields(data, context),
  num_expected_participants: Math.max(data.attendees!, context.participantCount),
  executive_hold: data.executiveHoldCalendars && data.executiveHoldCalendars.length > 0,
  recurring_times: data.recurringTimes,
  booking_calendar: data.selectedCalendar,
  booking_color: data.selectedColor,
  private_visibility: data.privateVisibility,
});
